var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PollAnswerType } from '@amityco/js-sdk';
import { useForm, Controller } from 'react-hook-form';
import useSocialMention from '../../../../social/hooks/useSocialMention';
import { PollComposerContainer, Form, FormBlockBody, FormBlockContainer, Field, ErrorMessage, FormBody, Footer, LabelContainer, SubmitButton, Label, ControllerContainer, FieldContainer, Counter, LabelWrapper, MentionTextInput, } from './styles';
import OptionsComposer from '../../../../social/components/post/PollComposer/OptionsComposer';
import { useAsyncCallback } from '../../../../core/hooks/useAsyncCallback';
import InputCounter, { COUNTER_VALUE_PLACEHOLDER } from '../../../../core/components/InputCounter';
import AnswerTypeSelector from '../../../../social/components/post/PollComposer/AnswerTypeSelector';
import useElement from '../../../../core/hooks/useElement';
import Button from '../../../../core/components/Button';
import { extractMetadata } from '../../../../helpers/utils';
import { MAXIMUM_MENTIONEES } from '../../../../social/constants';
import { info } from '../../../../core/components/Confirm';
const MAX_QUESTION_LENGTH = 500;
const MIN_OPTIONS_AMOUNT = 2;
const MAX_OPTIONS_AMOUNT = 10;
const MILLISECONDS_IN_DAY = 86400000;
const FormBlock = ({ children }) => (React.createElement(FormBlockContainer, null,
    React.createElement(FormBlockBody, null, children)));
const PollComposer = ({ className, targetId, targetType, onCancel = () => { }, onSubmit = () => { }, setDirtyExternal = () => { }, }) => {
    var _a;
    const { text, markup, mentions, queryMentionees, onChange: mentionOnChange, } = useSocialMention({ targetId, targetType });
    const defaultValues = {
        question: '',
        answers: [],
        answerType: PollAnswerType.Single,
        closedIn: 30,
    };
    const { handleSubmit, setError, watch, control, formState: { errors, isDirty }, } = useForm({
        defaultValues,
    });
    const { formatMessage } = useIntl();
    const question = watch('question', '');
    const answers = watch('answers', []);
    useEffect(() => setDirtyExternal(isDirty), [isDirty, setDirtyExternal]);
    const [validateAndSubmit, submitting] = useAsyncCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c, _d;
        if (!data.question.trim()) {
            setError('question', { message: 'Question cannot be empty' });
            return;
        }
        if (((_b = data === null || data === void 0 ? void 0 : data.answers) === null || _b === void 0 ? void 0 : _b.length) < MIN_OPTIONS_AMOUNT) {
            setError('answers', { message: `Minimum amount of answers should be ${MIN_OPTIONS_AMOUNT}` });
            return;
        }
        if (((_c = data === null || data === void 0 ? void 0 : data.answers) === null || _c === void 0 ? void 0 : _c.length) > MAX_OPTIONS_AMOUNT) {
            setError('answers', { message: `Maximum amount of answers should be ${MAX_OPTIONS_AMOUNT}` });
            return;
        }
        const { mentionees, metadata } = extractMetadata(mentions);
        const payload = {
            question: data === null || data === void 0 ? void 0 : data.question,
            answers: ((_d = data === null || data === void 0 ? void 0 : data.answers) === null || _d === void 0 ? void 0 : _d.length) ? data.answers : undefined,
            answerType: (data === null || data === void 0 ? void 0 : data.answerType) || PollAnswerType.Single,
            closedIn: data ? data.closedIn * MILLISECONDS_IN_DAY : undefined,
        };
        yield onSubmit(payload, mentionees, metadata);
    }));
    const disabled = !isDirty ||
        question.length === 0 ||
        answers.length === 0 ||
        answers.some((a) => { var _a; return ((_a = a.data) === null || _a === void 0 ? void 0 : _a.trim().length) === 0; }) ||
        submitting;
    const [formBodyRef, formBodyElement] = useElement();
    return (React.createElement(PollComposerContainer, null,
        React.createElement(Form, { className: className, onSubmit: handleSubmit(validateAndSubmit) },
            React.createElement(FormBody, { ref: formBodyRef },
                React.createElement(FormBlock, null,
                    React.createElement(Field, null,
                        React.createElement(LabelWrapper, null,
                            React.createElement(LabelContainer, null,
                                React.createElement(Label, { className: "required" },
                                    React.createElement(FormattedMessage, { id: "poll_composer.question.label" }))),
                            React.createElement(Counter, null, `${(_a = text === null || text === void 0 ? void 0 : text.length) !== null && _a !== void 0 ? _a : 0}/${MAX_QUESTION_LENGTH}`)),
                        React.createElement(Controller, { control: control, name: "question", rules: {
                                required: 'Question is required',
                                maxLength: {
                                    value: MAX_QUESTION_LENGTH,
                                    message: 'Question is too long',
                                },
                            }, render: (_a) => {
                                var _b = _a.field, { value, ref, onChange: pollOnChange } = _b, rest = __rest(_b, ["value", "ref", "onChange"]);
                                return (React.createElement(MentionTextInput, Object.assign({}, rest, { ref: ref, "data-qa-anchor": "poll-composer-options-textarea", mentionAllowed: true, multiline: true, value: markup, queryMentionees: queryMentionees, placeholder: formatMessage({ id: 'poll_composer.question.placeholder' }), onChange: (_a) => {
                                        var { plainText, mentions: mentionUsers } = _a, args = __rest(_a, ["plainText", "mentions"]);
                                        if ((mentionUsers === null || mentionUsers === void 0 ? void 0 : mentionUsers.length) > MAXIMUM_MENTIONEES) {
                                            return info({
                                                title: React.createElement(FormattedMessage, { id: "pollComposer.unableToMention" }),
                                                content: React.createElement(FormattedMessage, { id: "pollComposer.overMentionees" }),
                                                okText: React.createElement(FormattedMessage, { id: "pollComposer.okText" }),
                                                type: 'info',
                                            });
                                        }
                                        mentionOnChange(Object.assign({ plainText, mentions: mentionUsers }, args));
                                        pollOnChange(plainText);
                                    } })));
                            } }),
                        React.createElement(ErrorMessage, { errors: errors, name: "question" })),
                    React.createElement(Field, null,
                        React.createElement(LabelWrapper, null,
                            React.createElement(LabelContainer, null,
                                React.createElement(Label, { className: "required" },
                                    React.createElement(FormattedMessage, { id: "poll_composer.poll_options.label" }))),
                            React.createElement(Counter, null, `${answers.length}/${MAX_OPTIONS_AMOUNT}`)),
                        React.createElement(Controller, { rules: {
                                required: `There should be at least ${MIN_OPTIONS_AMOUNT} answers`,
                                minLength: {
                                    value: MIN_OPTIONS_AMOUNT,
                                    message: `There should be at least ${MIN_OPTIONS_AMOUNT} answers`,
                                },
                                maxLength: {
                                    value: MAX_OPTIONS_AMOUNT,
                                    message: `There can be only ${MAX_OPTIONS_AMOUNT} answers maximum`,
                                },
                            }, name: "answers", control: control, render: ({ field }) => (React.createElement(OptionsComposer, Object.assign({ optionsLimit: MAX_OPTIONS_AMOUNT }, field))), defaultValue: null }),
                        React.createElement(ErrorMessage, { errors: errors, name: "answers" })),
                    React.createElement(Field, { horizontal: true, separate: true },
                        React.createElement(FieldContainer, null,
                            React.createElement(LabelContainer, null,
                                React.createElement(Label, null,
                                    React.createElement(FormattedMessage, { id: "poll_modal.answer_type.title" })),
                                React.createElement("div", null,
                                    React.createElement(FormattedMessage, { id: "poll_modal.answer_type.body" }))),
                            React.createElement(ControllerContainer, null,
                                React.createElement(Controller, { rules: { required: 'Answer type is required' }, name: "answerType", render: ({ field }) => (React.createElement(AnswerTypeSelector, Object.assign({ parentContainer: formBodyElement }, field))), control: control, defaultValue: "" })))),
                    React.createElement(Field, { horizontal: true, separate: true },
                        React.createElement(FieldContainer, null,
                            React.createElement(LabelContainer, null,
                                React.createElement(Label, null,
                                    React.createElement(FormattedMessage, { id: "poll_modal.closed_in.title" })),
                                React.createElement("div", null,
                                    React.createElement(FormattedMessage, { id: "poll_modal.closed_in.body" }))),
                            React.createElement(ControllerContainer, null,
                                React.createElement(Controller, { name: "closedIn", render: ({ field }) => (React.createElement(InputCounter, Object.assign({}, field, { onlyPositiveNumber: true, minValue: 1, maxValue: 100, resultFormat: `${COUNTER_VALUE_PLACEHOLDER} days`, defaultValue: defaultValues.closedIn }))), control: control, defaultValue: defaultValues.closedIn })))))),
            React.createElement(Footer, null,
                React.createElement(Button, { onClick: (e) => {
                        e.preventDefault();
                        onCancel();
                    } },
                    React.createElement(FormattedMessage, { id: "cancel" })),
                React.createElement(SubmitButton, { "data-qa-anchor": "poll-composer-post-button", disabled: disabled },
                    React.createElement(FormattedMessage, { id: "post" }))))));
};
export default PollComposer;
//# sourceMappingURL=index.js.map